/**
 * ProposalEditor — inline editor for creating/updating a proposal revision.
 *
 * Features (F-2.0 Plan, Phase 6):
 * - Items table with ProductSearchInput (D3 spike confirmed)
 * - qty field, pvp_cents editable, cost_cents read-only (snapshot)
 * - PricePanel: margin slider + final price + zone badge (Phase 6.6)
 * - Real-time total calculation
 * - Notes textarea
 * - Validates price >= cost
 * - Sends POST /admin/orders/:id/proposal with optimistic lock (version)
 * - useDirtyState exit guard: prompts before discarding unsaved changes
 */
import React, { useCallback, useEffect, useState } from "react";
import { Box, IconButton, Snackbar, Alert, Tooltip } from "@mui/material";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import api from "../../hooks/api";
import { API_URLS } from "../../App";
import { Product as CatalogProduct } from "../common/ProductSearchInput";
import {
  ProposalDraftItem,
  ProposalRevision,
  CreateRevisionRequest,
  ProposalReferenceType,
  ProposalRole,
} from "../../model/Proposal";
import { useAuth } from "../../context/AuthContext";
import { deriveProposalRole } from "../../helpers/proposalApi";
import {
  StaleVersionDialog,
  StaleItemPriceDialog,
  StaleItemPrice,
} from "./StaleConflictDialog";
import PricePanel from "./PricePanel";
import { useDirtyState } from "../../hooks/useDirtyState";
import {
  priceFromMargin,
  calculateDefaultMargin,
} from "../../helpers/calculateMargin";
import ProposalItemsTable from "./ProposalItemsTable";
import ProposalEditorActions from "./ProposalEditorActions";
import DriftInspectorDrawer from "./DriftInspectorDrawer";
import type { ResolveDriftResponse, DriftItem } from "../../model/Proposal";

interface Props {
  orderId: string;
  /** Current optimistic-lock version from server (0 if no revision yet) */
  currentVersion: number;
  /** Pre-filled items (e.g. when revising from CHANGE_REQUESTED) */
  initialItems?: ProposalDraftItem[];
  initialNotes?: string;
  /** Reference type loaded from existing revision (null/undefined = legacy/new) */
  initialReferenceType?: ProposalReferenceType | null;
  onSaved: (newVersion: number) => void;
  onCancel?: () => void;
}

function newEmptyItem(): ProposalDraftItem {
  return {
    _localId: crypto.randomUUID(),
    product_id: "",
    name: "",
    pvp_cents: 0,
    cost_cents: 0,
    quantity: 1,
  };
}

function formatCents(cents: number): string {
  return (cents / 100).toFixed(2);
}

const ProposalEditor: React.FC<Props> = ({
  orderId,
  currentVersion,
  initialItems = [],
  initialNotes = "",
  initialReferenceType,
  onSaved,
  onCancel,
}) => {
  const { profile } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  // PR 6a Phase 6 — proposal role derived from the same Profile that drives
  // isAdmin. Invariant R1: this role MUST match the role used to build any
  // ProposalResponse passed downstream. Default "comercial" until profile
  // resolves — never escalates to admin by accident.
  const [proposalRole, setProposalRole] = useState<ProposalRole>("comercial");
  useEffect(() => {
    profile()
      .then((p) => {
        setIsAdmin(p.isAdmin?.() ?? false);
        // ProfileAndSettings nests the actual Profile (with .roles) under .profile.
        setProposalRole(deriveProposalRole(p.profile));
      })
      .catch(() => {
        setIsAdmin(false);
        setProposalRole("comercial");
      });
  }, [profile]);

  // F2-VR: reference type state. Default REV3 for all revisions without a
  // persisted reference_type (new AND legacy). Comercial never has cost_cents,
  // so defaulting to COST for legacy/pre-filled revisions causes 0.00 € in the
  // reference total. REV3 is the safe default for every role.
  const initialRefType: ProposalReferenceType = initialReferenceType ?? "REV3";
  const [referenceType, setReferenceType] =
    useState<ProposalReferenceType>(initialRefType);

  // F2-VR: COMMERCIAL users cannot switch reference type away from COST on
  // revisions that were originally created in COST mode (admin-only).
  const referenceLocked = !isAdmin && initialReferenceType === "COST";
  const [items, setItems] = useState<ProposalDraftItem[]>(
    initialItems.length > 0
      ? initialItems.map((i) => ({ ...i, _localId: crypto.randomUUID() }))
      : [newEmptyItem()],
  );
  const [notes, setNotes] = useState(initialNotes);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Task 7.3 — stale_version conflict dialog state
  const [staleVersionOpen, setStaleVersionOpen] = useState(false);
  const [serverRevision, setServerRevision] = useState<ProposalRevision | null>(
    null,
  );
  const [forcedVersion, setForcedVersion] = useState<number | null>(null);
  // Task 7.3 — stale_item_price dialog state
  const [staleItemsOpen, setStaleItemsOpen] = useState(false);
  const [staleItems, setStaleItems] = useState<StaleItemPrice[]>([]);
  const [pendingForceSave, setPendingForceSave] = useState(false);
  const [searchValues, setSearchValues] = useState<Record<string, string>>({});

  // PR 6b Phase 2 — drift drawer state
  const [driftDrawerOpen, setDriftDrawerOpen] = useState(false);
  const [driftSnackbar, setDriftSnackbar] = useState<string | null>(null);
  const [driftItems, setDriftItems] = useState<DriftItem[]>([]);

  // G3 — finalTotalCents is the ONLY price the user edits (via PricePanel).
  // It is decoupled from pvp_cents on items (which are catalog reference values).
  const catalogCostCents = items
    .filter((i) => i.product_id)
    .reduce((s, i) => s + i.cost_cents * i.quantity, 0);
  const catalogPvpCents = items
    .filter((i) => i.product_id)
    .reduce((s, i) => s + i.pvp_cents * i.quantity, 0);
  const [finalTotalCents, setFinalTotalCents] = useState<number | null>(null);
  // Initialise finalTotalCents from initialItems total or default margin
  useEffect(() => {
    if (finalTotalCents !== null) return; // already set
    const hasCatalogPvp = catalogPvpCents > 0;
    if (hasCatalogPvp) {
      setFinalTotalCents(catalogPvpCents);
    } else if (catalogCostCents > 0) {
      const dm = calculateDefaultMargin(catalogCostCents);
      setFinalTotalCents(priceFromMargin(catalogCostCents, dm));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogCostCents, catalogPvpCents]);

  // G3 — dirty state exit guard
  const dirtyState = useDirtyState({ items, notes });
  const { ExitGuardDialog } = dirtyState;

  // Keep dirty tracker in sync with editor state
  useEffect(() => {
    dirtyState.setCurrentValue({ items, notes });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, notes]);

  // Strip "builder:" prefix from a product_id before using as search query.
  // OMS builder-quote orders store product_id as "builder:<reference>" but the
  // Niposom catalog search expects only the bare reference (e.g. "A1700I3-12100FB").
  const stripBuilderPrefix = (id: string) => id.replace(/^builder:/, "");

  // Enrich all items that were loaded from an existing revision with full
  // catalog data: name, rev4_cents, pvp_cents, cost_cents. Runs once on mount.
  useEffect(() => {
    const itemsToEnrich = items.filter((i) => i.product_id);
    if (itemsToEnrich.length === 0) return;

    itemsToEnrich.forEach(async (item) => {
      try {
        const res = await api.get<{ results: CatalogProduct[] }>(
          API_URLS.COMPOUNDS + "/products/_search",
          { params: { search: stripBuilderPrefix(item.product_id), limit: 1 } },
        );
        const p = res.data?.results?.[0];
        if (!p) return;
        updateItem(item._localId, {
          name: item.name || p.name,
          rev4_cents: item.rev4_cents ?? Math.round((p.rev4_price ?? 0) * 100),
          rev3_cents: item.rev3_cents ?? Math.round((p.rev3_price ?? 0) * 100),
          pvp_cents:
            item.pvp_cents > 0
              ? item.pvp_cents
              : Math.round((p.pvp_price ?? p.rev4_price ?? 0) * 100),
          cost_cents:
            item.cost_cents > 0
              ? item.cost_cents
              : Math.round(p.cost_price * 100),
        });
      } catch {
        // non-fatal: item stays with existing values
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync searchValues when items change
  useEffect(() => {
    const next: Record<string, string> = {};
    items.forEach((i) => {
      next[i._localId] =
        searchValues[i._localId] ?? stripBuilderPrefix(i.product_id ?? "");
    });
    setSearchValues(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.map((i) => i._localId).join(",")]);

  const updateItem = useCallback(
    (localId: string, patch: Partial<ProposalDraftItem>) => {
      setItems((prev) =>
        prev.map((i) => (i._localId === localId ? { ...i, ...patch } : i)),
      );
    },
    [],
  );

  const removeItem = useCallback((localId: string) => {
    setItems((prev) => prev.filter((i) => i._localId !== localId));
  }, []);

  const addItem = useCallback(() => {
    setItems((prev) => [...prev, newEmptyItem()]);
  }, []);

  const validate = (): string | null => {
    const filled = items.filter((i) => i.product_id);
    if (filled.length === 0) return "Adiciona pelo menos 1 produto.";
    for (const item of filled) {
      if (item.quantity < 1)
        return `Qty inválida no produto ${item.product_id}.`;
    }
    const total = finalTotalCents ?? 0;
    if (total <= 0) return "Define o Preço Final da Proposta no painel abaixo.";
    if (total < catalogCostCents) {
      return `Preço final (${formatCents(total)} €) está abaixo do custo total (${formatCents(catalogCostCents)} €). A proposta geraria prejuízo.`;
    }
    return null;
  };

  const handleSave = async () => {
    const validationErr = validate();
    if (validationErr) {
      setError(validationErr);
      return;
    }
    setError(null);
    setSaving(true);
    try {
      const payload: CreateRevisionRequest = {
        version: forcedVersion ?? currentVersion,
        items: items
          .filter((i) => i.product_id)
          .map(({ _localId: _l, ...rest }) => rest),
        total_cents: finalTotalCents ?? catalogPvpCents,
        notes,
        reference_type: referenceType,
      };
      const res = await api.post<{ version: number }>(
        `${API_URLS.OMS_ADMIN_ORDERS}/${orderId}/proposal`,
        payload,
      );
      setForcedVersion(null);
      dirtyState.reset({ items, notes });
      onSaved(res.data.version);
    } catch (e: any) {
      const code = e?.response?.data?.error;
      if (code === "stale_version") {
        // 409 — fetch server's latest revision and show diff dialog
        const serverVersion: number =
          e.response.data.current_version ?? currentVersion + 1;
        try {
          const revRes = await api.get<ProposalRevision>(
            `${API_URLS.OMS_ADMIN_ORDERS}/${orderId}/proposal`,
          );
          setServerRevision(revRes.data);
        } catch {
          setServerRevision({
            version: serverVersion,
            items: [],
            total_cents: 0,
            notes: "",
            created_by: "",
            created_at: new Date().toISOString(),
          });
        }
        setStaleVersionOpen(true);
      } else if (code === "stale_item_price") {
        // 422 — highlight stale rows
        const rawItems: StaleItemPrice[] = e?.response?.data?.items ?? [];
        setStaleItems(rawItems);
        setStaleItemsOpen(true);
        setPendingForceSave(true);
      } else if (code === "price_below_cost") {
        setError("PVP total está abaixo do custo total dos artigos.");
      } else if (code === "price_below_reference") {
        const refLabel = referenceType === "REV3" ? "Rev3" : "custo";
        setError(
          `Preço final está abaixo do valor de referência (${refLabel}). Ajusta antes de guardar.`,
        );
      } else {
        setError(e?.response?.data?.error ?? "Erro ao guardar revisão.");
      }
    } finally {
      setSaving(false);
    }
  };

  // Task 7.3 — adopt server version: replace local items with server's
  const handleAdoptServer = () => {
    if (!serverRevision) return;
    const serverItems: ProposalDraftItem[] = (serverRevision.items ?? []).map(
      (i) => ({ ...i, _localId: crypto.randomUUID() }),
    );
    setItems(serverItems);
    setNotes(serverRevision.notes ?? "");
    setForcedVersion(serverRevision.version);
    setFinalTotalCents(serverRevision.total_cents || null);
    setStaleVersionOpen(false);
    setServerRevision(null);
  };

  // Task 7.3 — keep user changes but bump version to server's latest
  const handleKeepMine = (newForcedVersion: number) => {
    setForcedVersion(newForcedVersion);
    setStaleVersionOpen(false);
    setServerRevision(null);
  };

  // Task 7.3 — acknowledge stale prices and re-save
  const handleAcknowledgeStaleItems = async () => {
    setStaleItemsOpen(false);
    setPendingForceSave(false);
    if (pendingForceSave) {
      await handleSave();
    }
  };

  // G3 — cancel via exit guard
  const handleCancel = () => {
    if (!onCancel) return;
    dirtyState.requestExit(onCancel);
  };

  // PR 6b Phase 2 — drift resolved callback
  const handleDriftResolved = useCallback(
    (response: ResolveDriftResponse) => {
      setDriftDrawerOpen(false);
      setDriftItems([]);
      const strategyLabel: Record<ResolveDriftResponse["strategy"], string> = {
        accept_current: "Valores actuais aceites.",
        create_new_revision: "Nova revisão criada.",
        keep_snapshot: "Snapshot mantido.",
      };
      setDriftSnackbar(strategyLabel[response.strategy] ?? "Drift resolvido.");
      onSaved(response.new_revision_id ?? currentVersion);
    },
    [onSaved, currentVersion],
  );

  return (
    <Box>
      {/* PR 6b Phase 2 — "Ver drift" button, visible only when a revision exists */}
      {currentVersion > 0 && (
        <Box display="flex" justifyContent="flex-end" mb={1}>
          <Tooltip title="Ver relatório de drift de preços">
            <IconButton
              size="small"
              onClick={() => setDriftDrawerOpen(true)}
              aria-label="Ver relatório de drift de preços"
            >
              <CompareArrowsIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      <ProposalItemsTable
        items={items}
        searchValues={searchValues}
        setSearchValues={setSearchValues}
        updateItem={updateItem}
        removeItem={removeItem}
        addItem={addItem}
        driftItems={driftItems}
        onDriftClick={() => setDriftDrawerOpen(true)}
        isAdmin={isAdmin}
      />

      {/* G3 — PricePanel: único valor editável — Preço Final da Proposta (PVP Total ao cliente) */}
      {items.some((i) => i.product_id) && (
        <Box mb={2}>
          <PricePanel
            items={items.filter((i) => i.product_id)}
            externalPriceCents={finalTotalCents ?? undefined}
            onPriceChange={(cents) => setFinalTotalCents(cents)}
            referenceType={referenceType}
            onReferenceTypeChange={setReferenceType}
            isAdmin={isAdmin}
            referenceLocked={referenceLocked}
            role={proposalRole}
          />
        </Box>
      )}

      <ProposalEditorActions
        notes={notes}
        setNotes={setNotes}
        error={error}
        saving={saving}
        onCancel={onCancel}
        handleCancel={handleCancel}
        handleSave={handleSave}
      />

      {/* Task 7.3 — stale_version conflict dialog */}
      <StaleVersionDialog
        open={staleVersionOpen}
        userItems={items}
        userNotes={notes}
        userTotalCents={finalTotalCents ?? catalogPvpCents}
        serverRevision={serverRevision}
        onAdoptServer={handleAdoptServer}
        onKeepMine={handleKeepMine}
        onClose={() => setStaleVersionOpen(false)}
      />

      {/* Task 7.3 — stale_item_price dialog */}
      <StaleItemPriceDialog
        open={staleItemsOpen}
        staleItems={staleItems}
        onAcknowledge={handleAcknowledgeStaleItems}
        onClose={() => {
          setStaleItemsOpen(false);
          setPendingForceSave(false);
        }}
      />

      {/* G3 — exit guard dialog (renders when isDirty + requestExit triggered) */}
      <ExitGuardDialog />

      {/* PR 6b Phase 2 — drift inspector drawer */}
      <DriftInspectorDrawer
        open={driftDrawerOpen}
        onClose={() => setDriftDrawerOpen(false)}
        orderId={orderId}
        revisionVersion={currentVersion}
        role={proposalRole}
        onResolved={handleDriftResolved}
      />

      {/* PR 6b Phase 2 — drift resolve success snackbar */}
      <Snackbar
        open={driftSnackbar !== null}
        autoHideDuration={4000}
        onClose={() => setDriftSnackbar(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="success"
          onClose={() => setDriftSnackbar(null)}
          variant="filled"
        >
          {driftSnackbar}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProposalEditor;
